import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { driveEmbedUrl } from "@/lib/imageUtil";

/** Full-screen media viewer with prev/next + swipe + Escape close.
 *  Rendered inline (NOT portaled) so the parent Radix Dialog treats clicks
 *  inside the viewer as in-dialog and doesn't dismiss itself. The viewer
 *  uses position:fixed + a very high z-index to still cover the entire
 *  viewport visually. */
export default function MediaViewer({ items, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const touchStartX = useRef(null);

  useEffect(() => setIdx(startIndex), [startIndex]);

  useEffect(() => {
    // Use capture phase so we intercept Escape before Radix Dialog also
    // listens for it (otherwise pressing Esc closes both this viewer and the
    // parent profile dialog).
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, items.length - 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
    };
    document.addEventListener("keydown", onKey, { capture: true });
    return () => document.removeEventListener("keydown", onKey, { capture: true });
  }, [items.length, onClose]);

  if (!items || items.length === 0) return null;
  const item = items[idx];

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) setIdx((i) => Math.min(i + 1, items.length - 1));
      else setIdx((i) => Math.max(i - 1, 0));
    }
    touchStartX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/95 backdrop-blur-md flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.stopPropagation();
          onClose?.();
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid="media-viewer"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        paddingLeft: "env(safe-area-inset-left, 0px)",
      }}
    >
      {/* Prominent Close button — unmistakable on iPad */}
      <button
        className="absolute top-4 right-4 inline-flex items-center gap-2 h-12 px-4 rounded-full bg-[#ff5a5a]/20 border border-[#ff5a5a]/60 text-[#ff9a9a] hover:bg-[#ff5a5a]/30 hover:text-white transition-colors font-mono uppercase tracking-widest text-xs shadow-[0_0_24px_rgba(255,90,90,0.35)]"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        data-testid="media-viewer-close"
        aria-label="Close gallery and return to profile"
      >
        <X className="h-4 w-4" />
        Close
      </button>
      <div
        className="absolute top-4 left-4 font-mono text-xs text-white/60"
        onClick={(e) => e.stopPropagation()}
      >
        {idx + 1} / {items.length}
      </div>

      {items.length > 1 && (
        <button
          className="absolute left-2 md:left-6 h-12 w-12 rounded-full border border-white/15 text-white/70 hover:text-[#00e5ff] hover:border-[#00e5ff] flex items-center justify-center disabled:opacity-30"
          onClick={(e) => {
            e.stopPropagation();
            setIdx((i) => Math.max(i - 1, 0));
          }}
          disabled={idx === 0}
          data-testid="media-viewer-prev"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      <div
        className="max-w-[92vw] max-h-[88vh] w-full md:w-auto flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {item.type === "image" ? (
          <img
            src={item.data || item.url}
            alt={item.caption || ""}
            className="max-h-[88vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
            draggable={false}
          />
        ) : (
          <div className="w-[92vw] md:w-[80vw] aspect-video">
            <iframe
              title="video"
              src={driveEmbedUrl(item.url)}
              className="w-full h-full rounded-lg border border-white/10 bg-black"
              allow="autoplay"
              allowFullScreen
            />
          </div>
        )}
      </div>

      {items.length > 1 && (
        <button
          className="absolute right-2 md:right-6 h-12 w-12 rounded-full border border-white/15 text-white/70 hover:text-[#00e5ff] hover:border-[#00e5ff] flex items-center justify-center disabled:opacity-30"
          onClick={(e) => {
            e.stopPropagation();
            setIdx((i) => Math.min(i + 1, items.length - 1));
          }}
          disabled={idx === items.length - 1}
          data-testid="media-viewer-next"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {item.caption && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-xs text-white/80 bg-black/60 px-3 py-1.5 rounded-full">
          {item.caption}
        </div>
      )}
    </div>
  );
}
